import { z } from "zod";
import { CONTACT_STATUS_VALUES } from "@/lib/constants/contact-statuses";
import { MEETING_STATUS_VALUES } from "@/lib/constants/meeting-statuses";
import { TASK_STATUS_VALUES, TASK_PRIORITY_VALUES } from "@/lib/constants/task-enums";
import { PROPOSAL_STATUS_VALUES } from "@/lib/constants/proposal-statuses";
import { PROVIDER_KEY_VALUES } from "@/lib/integrations/email-providers/types/common-types";

// ============================================================================
// BASIC TYPE SCHEMAS
// ============================================================================

export const ImportanceSchema = z.enum(["low", "normal", "high"]);
export type Importance = z.infer<typeof ImportanceSchema>;
/** @deprecated Use Importance */
export type MessageImportance = Importance;

// Derived from the single PROVIDER_KEY_VALUES source (email-providers common-types) so the
// runtime Zod validator can never drift from the ProviderKey type.
export const ProviderKeySchema = z.enum(PROVIDER_KEY_VALUES);
export type ProviderKey = z.infer<typeof ProviderKeySchema>;

// ============================================================================
// PARTICIPANT SCHEMAS
// ============================================================================

export const SenderSchema = z.object({
  name: z.string(),
  email: z.string().email(),
  avatar: z.string().optional(),
}).strict();

export type Sender = z.infer<typeof SenderSchema>;

export const RecipientSchema = z.object({
  name: z.string(),
  email: z.string().email(),
}).strict();

export type Recipient = z.infer<typeof RecipientSchema>;

// ============================================================================
// STATUS & ENUM SCHEMAS
// ============================================================================

// Matches the Postgres `contact_status` enum (contacts.status). "active" is NOT
// a valid contact_status value — it was accepted here in error.
export const ContactStatusSchema = z.enum(CONTACT_STATUS_VALUES);
export type ContactStatus = z.infer<typeof ContactStatusSchema>;

export const ContactTypeSchema = z.enum(["client", "prospect", "vendor", "partner"]);
export type ContactType = z.infer<typeof ContactTypeSchema>;

export const TaskStatusSchema = z.enum(TASK_STATUS_VALUES);
export type TaskStatus = z.infer<typeof TaskStatusSchema>;

export const TaskPrioritySchema = z.enum(TASK_PRIORITY_VALUES);
export type TaskPriority = z.infer<typeof TaskPrioritySchema>;

// Matches the Postgres `meeting_status` enum (contact_meetings.status).
export const MeetingStatusSchema = z.enum(MEETING_STATUS_VALUES);
export type MeetingStatus = z.infer<typeof MeetingStatusSchema>;

// Matches the Postgres `proposal_status` enum (contact_proposals.status).
export const ProposalStatusSchema = z.enum(PROPOSAL_STATUS_VALUES);
export type ProposalStatus = z.infer<typeof ProposalStatusSchema>;

export const ClientStatusSchema = z.enum(["active", "inactive", "suspended", "terminated"]);
export type ClientStatus = z.infer<typeof ClientStatusSchema>;
